import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Truck,
    Package,
    Clock,
    CheckCircle,
    Home,
    ArrowLeft,
    Download,
    Mail,
    Phone,
    FileText,
    ShoppingBag,
    User,
    AlertCircle,
    ChevronRight,
    ChevronDown
} from 'lucide-react';
import { OrderService } from "../../api/order.api";
import { fadeInUp, pageTransition } from "../animations/animations";
import Navbar from "./Navbar";
import AccountHeroBit from "../account/AccountHeroBit";
import LuxuryInput from "../ui/LuxuryInput";
import LuxuryButton from "../ui/LuxuryButton";
import Footer from "./Footer";
import {designTokens} from "../../theme/designTokens";

const OrderTracking = () => {
    const navigate = useNavigate();
    const { orderNumber } = useParams();

    const [trackingInfo, setTrackingInfo] = useState({
        orderNumber: orderNumber || '',
        email: '',
        phone: ''
    });
    const [trackingResults, setTrackingResults] = useState(null);
    const [isTracking, setIsTracking] = useState(false);
    const [error, setError] = useState(null);
    const [expandedSection, setExpandedSection] = useState(null);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Check if order number is in URL
    useEffect(() => {
        if (orderNumber) {
            setTrackingInfo(prev => ({ ...prev, orderNumber }));
        }
    }, [orderNumber]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsTracking(true);
        setError(null);

        try {
            const result = await OrderService.trackOrderByNumber(
                trackingInfo.orderNumber,
                trackingInfo.email,
                trackingInfo.phone
            );
            setTrackingResults(result);
        } catch (err) {
            setError(err.message || 'Failed to track order. Please check your details.');
            setTrackingResults(null);
        } finally {
            setIsTracking(false);
        }
    };

    const handleDownloadInvoice = async () => {
        try {
            await OrderService.downloadInvoice(trackingResults.orderNumber);
        } catch (err) {
            setError('Failed to download invoice. Please try again.');
        }
    };

    const getStatusIcon = (status) => {
        switch (status?.toLowerCase()) {
            case 'confirmed':
            case 'processing':
                return <Clock className="w-5 h-5" />;
            case 'shipped':
                return <Truck className="w-5 h-5" />;
            case 'delivered':
                return <CheckCircle className="w-5 h-5" />;
            default:
                return <Package className="w-5 h-5" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'confirmed':
                return 'bg-champagne/20 text-champagne border-champagne/30';
            case 'processing':
                return 'bg-amber-100 text-amber-800 border-amber-200';
            case 'shipped':
                return 'bg-blue-100 text-blue-800 border-blue-200';
            case 'delivered':
                return 'bg-green-100 text-green-800 border-green-200';
            case 'cancelled':
                return 'bg-red-100 text-red-800 border-red-200';
            default:
                return 'bg-nude/30 text-charcoal/70 border-nude/50';
        }
    };

    const getStatusProgress = (status) => {
        const statuses = ['confirmed', 'processing', 'shipped', 'delivered'];
        const currentIndex = statuses.indexOf(status?.toLowerCase());
        return currentIndex >= 0 ? ((currentIndex + 1) / statuses.length) * 100 : 25;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatShortDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric'
        });
    };

    const toggleSection = (section) => {
        setExpandedSection(expandedSection === section ? null : section);
    };

    // Order status timeline
    const statusSteps = [
        { key: 'confirmed', label: 'Order Confirmed', description: 'Your order has been received' },
        { key: 'processing', label: 'Processing', description: 'Preparing your order for shipment' },
        { key: 'shipped', label: 'Shipped', description: 'Your order is on the way' },
        { key: 'delivered', label: 'Delivered', description: 'Order delivered successfully' }
    ];

    return (
        <div className="min-h-screen bg-gradient-to-b from-pearl to-nude/20">
            <Navbar />

            <motion.div
                variants={pageTransition}
                initial="initial"
                animate="animate"
                exit="exit"
                className="min-h-screen py-12 pt-32 pb-16"
            >
                <div className="container-luxury max-w-6xl mx-auto px-4">
                    {/* Hero Section */}
                    <AccountHeroBit
                        title="Order Tracking"
                        subtitle="Track your KROMAIC order in real-time"
                    >
                        <motion.div
                            variants={fadeInUp}
                            className="flex flex-wrap items-center gap-4 mt-6"
                        >
                            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full">
                                <Truck className="w-4 h-4 text-champagne" />
                                <span className="text-charcoal/70">Real-time Tracking</span>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full">
                                <Package className="w-4 h-4 text-sage" />
                                <span className="text-charcoal/70">Status Updates</span>
                            </div>
                            <div className="flex items-center gap-2 px-4 py-2 bg-white/50 rounded-full">
                                <Clock className="w-4 h-4 text-champagne" />
                                <span className="text-charcoal/70">Estimated Delivery</span>
                            </div>
                        </motion.div>
                    </AccountHeroBit>

                    {/* Main Content */}
                    <div className="grid lg:grid-cols-3 gap-8 mt-12">
                        {/* Left Column - Tracking Form */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Tracking Form */}
                            <motion.div
                                variants={fadeInUp}
                                className="luxury-card p-8"
                            >
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-champagne/20 to-sage/20 flex items-center justify-center">
                                        <Truck className="w-6 h-6 text-champagne" />
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-medium text-charcoal">Track Your Order</h3>
                                        <p className="text-charcoal/60">Enter your order details to view status</p>
                                    </div>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-charcoal/80 mb-2">
                                                Order Number *
                                            </label>
                                            <LuxuryInput
                                                type="text"
                                                value={trackingInfo.orderNumber}
                                                onChange={(e) => setTrackingInfo(prev => ({ ...prev, orderNumber: e.target.value }))}
                                                placeholder="Enter your order number (e.g., KRO2601305607)"
                                                icon={<Package />}
                                                required
                                            />
                                        </div>


                                        <p className="text-sm text-charcoal/50">
                                            * Find your order number in your confirmation email or SMS
                                        </p>
                                    </div>

                                    <div className="flex flex-col sm:flex-row gap-4">
                                        <LuxuryButton
                                            type="submit"
                                            variant="primary"
                                            size="large"
                                            loading={isTracking}
                                            disabled={!trackingInfo.orderNumber.trim() || isTracking}
                                            style={{
                                                background: designTokens.colors.gradients.champagne,
                                            }}
                                        >
                                            {isTracking ? 'Tracking...' : 'Track Order'}
                                        </LuxuryButton>
                                    </div>
                                </form>
                            </motion.div>

                            {/* Error State */}
                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -20 }}
                                        className="luxury-card p-6 bg-error/10 border border-error/20"
                                    >
                                        <div className="flex items-start gap-4">
                                            <div className="w-10 h-10 rounded-full bg-error/20 flex items-center justify-center flex-shrink-0">
                                                <AlertCircle className="w-5 h-5 text-error" />
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="font-medium text-error mb-2">Unable to Track Order</h4>
                                                <p className="text-error/80 text-sm mb-3">{error}</p>
                                                <div className="flex flex-wrap gap-3">
                                                    <button
                                                        onClick={() => setError(null)}
                                                        className="text-sm text-error hover:text-error/80 transition-colors"
                                                    >
                                                        Try Again
                                                    </button>
                                                    <Link
                                                        to="/contact"
                                                        className="text-sm text-charcoal/60 hover:text-charcoal transition-colors"
                                                    >
                                                        Need Help? Contact Support
                                                    </Link>
                                                </div>
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Right Column - Help & Tips */}
                        <div className="space-y-6">
                            <motion.div
                                variants={fadeInUp}
                                transition={{ delay: 0.1 }}
                                className="luxury-card p-6"
                            >
                                <h3 className="font-medium text-charcoal mb-4">How to Find Your Order Number</h3>
                                <div className="space-y-4">
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center flex-shrink-0">
                                            <span className="text-xs font-medium text-champagne">1</span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-charcoal">Check Your Email</p>
                                            <p className="text-xs text-charcoal/50">Look for order confirmation email from KROMAIC</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center flex-shrink-0">
                                            <span className="text-xs font-medium text-champagne">2</span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-charcoal">Check SMS</p>
                                            <p className="text-xs text-charcoal/50">Look for order confirmation message on your phone</p>
                                        </div>
                                    </div>
                                    <div className="flex items-start gap-3">
                                        <div className="w-8 h-8 rounded-full bg-champagne/10 flex items-center justify-center flex-shrink-0">
                                            <span className="text-xs font-medium text-champagne">3</span>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-charcoal">Check Your Account</p>
                                            <Link
                                                to="/login"
                                                className="text-xs text-champagne hover:text-champagne/80 transition-colors"
                                            >
                                                Login to view your orders →
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>

                        </div>
                    </div>

                    {/* Tracking Results */}
                    <AnimatePresence>
                        {trackingResults && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.3 }}
                                className="mt-12 space-y-8"
                            >
                                {/* Order Header */}
                                <div className="luxury-card p-8">
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-8">
                                        <div>
                                            <h2 className="text-2xl font-medium text-charcoal mb-2">Order #{trackingResults.orderNumber}</h2>
                                            <div className="flex flex-wrap items-center gap-4">
                                                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border ${getStatusColor(trackingResults.status)}`}>
                                                    {getStatusIcon(trackingResults.status)}
                                                    <span className="font-medium">{trackingResults.status?.charAt(0).toUpperCase() + trackingResults.status?.slice(1)}</span>
                                                </div>
                                                <div className="text-sm text-charcoal/60">
                                                    Last updated: {formatDate(trackingResults.lastUpdated)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="mb-8">
                                        <div className="flex justify-between mb-2">
                                            <span className="text-sm font-medium text-charcoal">Order Progress</span>
                                            <span className="text-sm text-charcoal/60">{getStatusProgress(trackingResults.status)}%</span>
                                        </div>
                                        <div className="h-2 bg-nude/30 rounded-full overflow-hidden">
                                            <motion.div
                                                initial={{ width: 0 }}
                                                animate={{ width: `${getStatusProgress(trackingResults.status)}%` }}
                                                transition={{ duration: 1, ease: "easeOut" }}
                                                className="h-full bg-gradient-to-r from-champagne to-sage"
                                            />
                                        </div>
                                    </div>

                                    {/* Status Timeline */}
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                        {statusSteps.map((step, index) => {
                                            const isActive = statusSteps.findIndex(s => s.key === trackingResults.status?.toLowerCase()) >= index;
                                            const isCurrent = step.key === trackingResults.status?.toLowerCase();
                                            return (
                                                <div
                                                    key={step.key}
                                                    className={`relative p-4 rounded-xl border-2 ${isActive ? 'border-champagne/50 bg-champagne/5' : 'border-nude/30 bg-white/50'}`}
                                                >
                                                    {isCurrent && (
                                                        <motion.div
                                                            initial={{ scale: 0 }}
                                                            animate={{ scale: 1 }}
                                                            className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-champagne flex items-center justify-center"
                                                        >
                                                            <Truck className="w-4 h-4 text-white" />
                                                        </motion.div>
                                                    )}
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${isActive ? 'bg-champagne/20 text-champagne' : 'bg-nude/20 text-charcoal/40'}`}>
                                                        {getStatusIcon(step.key)}
                                                    </div>
                                                    <h4 className="font-medium text-charcoal mb-1">{step.label}</h4>
                                                    <p className="text-xs text-charcoal/50">{step.description}</p>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* What's Next Section */}
                                <motion.div
                                    variants={fadeInUp}
                                    transition={{ delay: 0.3 }}
                                    className="luxury-card p-8 text-center"
                                >
                                    <h3 className="text-xl font-medium text-charcoal mb-4">What's Next?</h3>
                                    <div className="grid md:grid-cols-3 gap-6">
                                        <div className="space-y-3">
                                            <div className="w-12 h-12 rounded-full bg-champagne/10 flex items-center justify-center mx-auto">
                                                <Truck className="w-6 h-6 text-champagne" />
                                            </div>
                                            <h4 className="font-medium text-charcoal">Track Your Package</h4>
                                            <p className="text-sm text-charcoal/60">
                                                Use the tracking number above to follow your package in real-time
                                            </p>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center mx-auto">
                                                <Package className="w-6 h-6 text-sage" />
                                            </div>
                                            <h4 className="font-medium text-charcoal">Receive Your Order</h4>
                                            <p className="text-sm text-charcoal/60">
                                                Our products are carefully packaged to ensure they arrive in perfect condition
                                            </p>
                                        </div>
                                        <div className="space-y-3">
                                            <div className="w-12 h-12 rounded-full bg-charcoal/10 flex items-center justify-center mx-auto">
                                                <CheckCircle className="w-6 h-6 text-charcoal" />
                                            </div>
                                            <h4 className="font-medium text-charcoal">Enjoy Your Products</h4>
                                            <p className="text-sm text-charcoal/60">
                                                Start your KROMAIC ritual and experience the transformation
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Empty State */}
                    <AnimatePresence>
                        {!trackingResults && !error && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="mt-12 text-center"
                            >
                                <div className="inline-flex items-center justify-center mb-6">
                                    <div className="relative">
                                        <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-champagne/10 to-sage/10 flex items-center justify-center">
                                            <Package className="w-12 h-12 text-charcoal/30" />
                                        </div>
                                        <div className="absolute -top-2 -right-2 w-10 h-10 rounded-full bg-sage/10 flex items-center justify-center">
                                            <Truck className="w-5 h-5 text-sage" />
                                        </div>
                                    </div>
                                </div>
                                <h3 className="text-2xl font-light text-charcoal mb-4">Ready to Track Your Order?</h3>
                                <p className="text-charcoal/60 max-w-md mx-auto mb-8">
                                    Enter your order number above to check the status, view tracking information, and get estimated delivery dates.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                    <Link to="/shop">
                                        <LuxuryButton
                                            variant="outline"
                                            size="large"
                                            icon={<ShoppingBag />}
                                            style={{
                                                background: designTokens.colors.gradients.champagne,
                                            }}
                                        >
                                            Continue Shopping
                                        </LuxuryButton>
                                    </Link>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </motion.div>

            <Footer />
        </div>
    );
};

export default OrderTracking;